<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import {computed, ref} from "vue";
import {useWindowSize} from '@vueuse/core';
import {useI18n} from 'vue-i18n';

const {t} = useI18n(); // use the i18n instance

const {width} = useWindowSize();
const isMobile = computed(() => width.value <= 768);

import { getAsset } from "@/helpers/assetsUtils";

const showPassword = ref(false);
const showConfirmPassword = ref(false);

const showConfirmPasswordIcon = computed(() => {
  return showConfirmPassword.value ? getAsset('icons', 'Hide_24x24.svg') : getAsset('icons', 'Show_24x24.svg');
});

const newPassword = ref('');
const confirmPassword = ref('');
const passwordsMatch = computed(() => newPassword.value === confirmPassword.value);
const passwordCriteriaMet = computed(() => /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/.test(newPassword.value));

const passwordFieldClass = computed(() => {
  return passwordsMatch.value && passwordCriteriaMet.value ? 'border-green-500' : 'border-red-500';
});

const showError = computed(() => !passwordsMatch.value || !passwordCriteriaMet.value);
</script>

<template>
  <div class="reset-col flex w-full min-h-[78vh] overflow-y-hidden">
    <div class="reset-left-col bg-primary-account flex items-center justify-center w-2/4 relative">
      <div class="self-start top-9 left-14 flex flex-row items-center font-semibold cursor-pointer absolute"
           @click="$router.back()">
        <img src="@/assets/icons/svg/Left_24x24.svg" alt="back" class="mr-2">
        {{ t('resetPassword.backButton') }}
      </div>
      <img src="@/assets/images/guitar.svg" alt="guitar" class="w-3/4"/>
    </div>
    <div class="reset-right-col flex flex-col w-2/4 justify-center items-center">
      <div class="reset-container w-1/2">
        <h1 class="form-title__text responsive-title-4 font-semibold self-start">{{ t('resetPassword.title') }}</h1>
        <h4 class="reset-password__text text-md mt-6 font-light text-black/50">
          {{ t('resetPassword.instruction') }}
        </h4>
        <div class="w-full">
          <VTextField v-model="newPassword" :label="t('resetPassword.newPasswordLabel')" variant="underlined"
                      :type="showPassword ? 'text' : 'password'" :class="passwordFieldClass">
            <template v-slot:append-inner>
              <div class="w-6 cursor-pointer" @click="showPassword = !showPassword" v-if="showPassword">
                <img src="@/assets/icons/svg/Hide_24x24.svg" alt='hide' width="64">
              </div>
              <div class="w-6 cursor-pointer" @click="showPassword = !showPassword" v-else>
                <img src="@/assets/icons/svg/Show_24x24.svg" alt='show' width="64">
              </div>
            </template>
          </VTextField>
          <VTextField v-model="confirmPassword" :label="t('resetPassword.confirmPasswordLabel')" variant="underlined"
                      :type="showConfirmPassword ? 'text' : 'password'" :class="passwordFieldClass">
            <template v-slot:append-inner>
              <div class="w-6 cursor-pointer" @click="showConfirmPassword = !showConfirmPassword">
                <img :src="showConfirmPasswordIcon" :alt="showConfirmPassword ? 'hide' : 'show'" width="64">
              </div>
            </template>
          </VTextField>
        </div>
        <div v-if="showError" class="mismatch-text  responsive-body-medium  text-red-500 my-2">
          {{ t('resetPassword.passwordMismatchError') }}
        </div>
        <CustomButton size="large" type="black" :rounded="true"
                      class="form-reset__btn w-32vw] font-semibold  responsive-body-large mt-3"
        >{{ t('resetPassword.resetButton') }}
        </CustomButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.border-red-500 {
  border-color: #f56565;
}

.border-green-500 {
  border-color: #48bb78;
}
.form-reset__btn {
  font-size: 1rem !important;
}
@media (max-width: 1538px) {
  .form-title__text {
    font-size: 1.5rem;
  }
  .mismatch-text {
    font-size: .875rem;
  }
}

@media (max-width: 1385px) {
  .reset-container {
    width: 65%;
  }
  .form-reset__btn {
    font-size: 1rem;
  }
  .mismatch-text {
    font-size: .875rem;
  }
}

@media (max-width: 1024px) {
  .reset-container {
    width: 60%;
  }

  .form-title__text {
    font-size: 1.5rem;
  }
  .form-reset__btn {
    width: 18vw;
  }
  .reset-password__text {
    font-size: .875rem;
  }
}

@media (max-width: 768px) {
  .btn {
    font-size: 12px;
  }

  .reset-col {
    margin-top: 2rem;
    min-height: unset;
  }

  .reset-left-col {
    display: none;
  }

  .reset-right-col {
    width: 100%;
    height: 100%;
  }

  .reset-container {
    width: 80%;
  }
  . .reset-password__text {
    font-size: .75rem;
  }
  .form-title__text {
    font-size: 1.25rem;
  }
  .form-reset__btn {
    width: 100%;
    font-size: .875rem;
  }
  .mismatch-text,
  .no-account__text {
    font-size: .75rem;
  }
}

</style>
